import { useState } from "react";
import { Button, isPasskeySupported, registerPasskey } from "@shop-ledger/ui-components";
import type { WebAuthnCredentialOut } from "@shop-ledger/shared-types";
import { useAuth } from "../../auth/AuthContext";
import * as authApi from "../../auth/authApi";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

/** A staff member's own account settings — today, just passkey management (add a
 * fingerprint/Face ID/security-key passkey for faster sign-in, or remove one). Every
 * OWNER/BRANCH_MANAGER/ACCOUNTANT/TELLER/ECOM_MGR reaches this the same way, from
 * `NavHeader`'s "My account" link — this is about YOUR OWN login, not staff
 * management (see `StaffPage.tsx` for managing other people's accounts). */
export function MyAccountPage() {
  const { user, ensureFreshToken } = useAuth();
  const credentialsState = useAsyncData(
    () => ensureFreshToken().then((token) => authApi.webauthnListCredentials(token)),
    [],
  );
  const [addPending, setAddPending] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<WebAuthnCredentialOut | null>(null);

  async function handleAddPasskey() {
    setAddError(null);
    setAddPending(true);
    try {
      const token = await ensureFreshToken();
      const { session_id, options } = await authApi.webauthnRegisterBegin(token);
      const credential = await registerPasskey(options);
      await authApi.webauthnRegisterComplete(token, {
        session_id,
        credential: credential as Record<string, unknown>,
      });
      credentialsState.reload();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error && error.name === "PasskeyCancelledError"
            ? "Passkey setup was cancelled or timed out."
            : "Could not add a passkey.";
      setAddError(message);
    } finally {
      setAddPending(false);
    }
  }

  async function handleConfirmRemove() {
    if (!pendingRemove) return;
    setRemovingId(pendingRemove.id);
    try {
      const token = await ensureFreshToken();
      await authApi.webauthnDeleteCredential(token, pendingRemove.id);
      setPendingRemove(null);
      credentialsState.reload();
    } catch (error) {
      setAddError(error instanceof ApiError ? error.message : "Could not remove that passkey.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">My account</h1>
          <p className="page__subtitle">{user?.email}</p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Passkeys</h2>
        <p className="muted" style={{ marginBottom: 14 }}>
          Sign in with Face ID, a fingerprint, Windows Hello, or a security key instead of typing your password.
        </p>

        {!isPasskeySupported() ? (
          <StatusBanner kind="info">This browser doesn't support passkeys.</StatusBanner>
        ) : null}

        {addError ? <StatusBanner kind="error">{addError}</StatusBanner> : null}

        {credentialsState.error ? <StatusBanner kind="error">{credentialsState.error}</StatusBanner> : null}
        {credentialsState.loading ? (
          <p className="muted">Loading…</p>
        ) : credentialsState.data && credentialsState.data.length > 0 ? (
          <ul className="checklist" style={{ marginBottom: 14 }}>
            {credentialsState.data.map((cred) => (
              <li key={cred.id} className="checklist__item">
                <span style={{ flex: 1 }}>
                  <strong>{cred.nickname}</strong>
                  <br />
                  <span className="muted" style={{ fontSize: "0.78rem" }}>
                    Added {new Date(cred.created_at).toLocaleDateString()}
                    {cred.last_used_at ? ` · last used ${new Date(cred.last_used_at).toLocaleDateString()}` : ""}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="danger"
                  size="md"
                  disabled={removingId === cred.id}
                  onClick={() => setPendingRemove(cred)}
                >
                  {removingId === cred.id ? "Removing…" : "Remove"}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted" style={{ marginBottom: 14 }}>
            No passkeys yet.
          </p>
        )}

        {pendingRemove ? (
          <div className="panel" style={{ marginBottom: 14 }}>
            <h2 className="panel__title">Remove "{pendingRemove.nickname}"?</h2>
            <p className="muted">
              {credentialsState.data && credentialsState.data.length === 1
                ? "This is your only passkey — after removing it you'll need your password to sign in."
                : "You'll no longer be able to sign in with this passkey."}
            </p>
            <div className="form-actions">
              <Button variant="danger" type="button" disabled={removingId === pendingRemove.id} onClick={() => void handleConfirmRemove()}>
                {removingId === pendingRemove.id ? "Removing…" : "Remove passkey"}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setPendingRemove(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {isPasskeySupported() ? (
          <Button type="button" variant="primary" size="md" disabled={addPending} onClick={() => void handleAddPasskey()}>
            {addPending ? "Waiting for your passkey…" : "Add a passkey"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
